import React, { useMemo, useState } from 'react';
import { AtSignIcon, CheckIcon, MessageSquareIcon, ReplyIcon, Trash2Icon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Field';
import { useNotes } from '../../contexts/NotesContext';
import { OWNER_HANDLE } from '../../data/notes';
import type { Note, ShareRole } from '../../types/notes';

/** Render @handles as mention chips when they resolve to someone with access. */
function withMentions(body: string, allowed: Set<string>) {
  return body.split(/(@[a-z0-9_.]+)/gi).map((part, index) => {
    if (part.startsWith('@') && allowed.has(part.slice(1).toLowerCase())) {
      return <span key={index} className="rounded bg-brand-50 px-1 font-medium text-brand-800" data-id="element-3458" data-mp="1so9gbxr1btt2">
          {part}
        </span>;
    }
    return <React.Fragment key={index} data-id="element-3459" data-mp="26m7rkua27a4c" data-mpi={index}>{part}</React.Fragment>;
  });
}
export function CommentsPanel({
  note,
  role
}: {
  note: Note;
  role: ShareRole;
}) {
  const {
    addComment,
    toggleCommentResolved,
    deleteComment
  } = useNotes();
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const comments = note.comments ?? [];
  const open = comments.filter(comment => !comment.resolved);
  const resolved = comments.filter(comment => comment.resolved);

  /** Only people with access can be mentioned — a mention never grants access. */
  const mentionable = useMemo(() => {
    const handles = new Set<string>();
    note.shares.forEach(share => handles.add(share.handle));
    return handles;
  }, [note.shares]);
  const canComment = role === 'owner' || role === 'editor';
  const visible = showResolved ? [...open, ...resolved] : open;
  return <section className="mx-auto max-w-[68ch] border-t border-line px-5 py-5 sm:px-8" data-id="element-3460" data-mp="2pg354bzd8wso">
      <div className="flex flex-wrap items-center gap-2" data-id="element-3461" data-mp="2uhin1v68yn6u">
        <MessageSquareIcon className="h-4 w-4 shrink-0 text-ink-subtle" data-id="element-3462" data-mp="24sbyszukewei" />
        <h2 className="text-[14px] font-semibold text-ink" data-id="element-3463" data-mp="1zuads9dkwn0e">
          Comments {open.length > 0 && <span className="text-ink-muted" data-id="element-3464" data-mp="2eiy523ge6akj">({open.length} open)</span>}
        </h2>
        {resolved.length > 0 && <button type="button" onClick={() => setShowResolved(value => !value)} className="ml-auto text-[12px] font-medium text-brand-800 hover:underline" data-id="element-3465" data-mp="29roixcu1xpde">
            {showResolved ? 'Hide' : 'Show'} {resolved.length} resolved
          </button>}
      </div>

      {canComment && <form onSubmit={event => {
      event.preventDefault();
      if (!draft.trim()) return;
      addComment(note.id, draft.trim());
      setDraft('');
    }} className="mt-3" data-id="element-3466" data-mp="2xhaii1ec66lx">
          <Textarea rows={2} value={draft} onChange={event => setDraft(event.target.value)} placeholder="Leave a comment. Type @ to mention someone with access…" aria-label="New comment" data-id="element-3467" data-mp="y4oklkf9t5yz" />
          <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-3468" data-mp="3eqbnms6oh4h1">
            <Button type="submit" size="sm" variant="primary" disabled={!draft.trim()} data-id="element-3469" data-mp="3017497o5cu7c">
              Comment
            </Button>
            <span className="flex flex-wrap items-center gap-1.5 text-[12px] text-ink-muted" data-id="element-3470" data-mp="34zh0zmpis2v2">
              <AtSignIcon className="h-3.5 w-3.5" data-id="element-3471" data-mp="2qzz9gzkuabi0" />
              {mentionable.size > 0 ? [...mentionable].map(handle => <button key={handle} type="button" onClick={() => setDraft(value => `${value}${value ? ' ' : ''}@${handle} `)} className="rounded border border-line-strong px-1.5 py-0.5 font-medium text-ink-muted hover:border-brand-700 hover:text-brand-800" data-id="element-3472" data-mp="2h3mezzhgztm7">
                    @{handle}
                  </button>) : <span data-id="element-3473" data-mp="2gdfvi61ktlqf">Share this note to mention someone.</span>}
            </span>
          </div>
        </form>}

      {visible.length === 0 ? <p className="mt-3 text-[13px] text-ink-muted" data-id="element-3474" data-mp="27chqbkfej869">
          {canComment ? 'No comments yet. Use them to ask a question or leave yourself a note for later.' : 'No comments on this note.'}
        </p> : <ul className="mt-4 space-y-3" data-id="element-3475" data-mp="1xhg2crwv4c59">
          {visible.map(comment => <li key={comment.id} className={`rounded-card border px-4 py-3 ${comment.resolved ? 'border-line bg-canvas opacity-75' : 'border-line-strong bg-surface'}`} data-id="element-3476" data-mp="1sn3hcdfqmsre">
              <div className="flex flex-wrap items-center gap-2" data-id="element-3477" data-mp="1eolvkl9e7qc4">
                <p className="text-[13px] font-semibold text-ink" data-id="element-3478" data-mp="1eo3th801uhwa">{comment.authorName}</p>
                <span className="cz-num text-[12px] text-ink-subtle" data-id="element-3479" data-mp="19qaj02rpr1q7">{comment.postedOn}</span>
                {comment.resolved && <Badge tone="ok" data-id="element-3480" data-mp="3lf8d829k4c9f">Resolved</Badge>}
                {canComment && <span className="ml-auto flex items-center gap-1" data-id="element-3481" data-mp="3qadyqzz1rowz">
                    <button type="button" onClick={() => toggleCommentResolved(note.id, comment.id)} aria-label={comment.resolved ? 'Reopen comment' : 'Resolve comment'} className="rounded p-1 text-ink-subtle hover:bg-canvas hover:text-ok-700" data-id="element-3482" data-mp="3vcd2iayp8nug">
                      <CheckIcon className="h-3.5 w-3.5" data-id="element-3483" data-mp="37jd4uqcsajka" />
                    </button>
                    <button type="button" onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)} aria-label="Reply" className="rounded p-1 text-ink-subtle hover:bg-canvas hover:text-ink" data-id="element-3484" data-mp="3bl829jrlgcss">
                      <ReplyIcon className="h-3.5 w-3.5" data-id="element-3485" data-mp="3r8ntk9kdwkw4" />
                    </button>
                    {comment.authorHandle === OWNER_HANDLE && <button type="button" onClick={() => deleteComment(note.id, comment.id)} aria-label="Delete comment" className="rounded p-1 text-ink-subtle hover:bg-danger-50 hover:text-danger-700" data-id="element-3486" data-mp="3m8rqccjigxhm">
                        <Trash2Icon className="h-3.5 w-3.5" data-id="element-3487" data-mp="38bw1pbqs0xgg" />
                      </button>}
                  </span>}
              </div>

              <p className="mt-1 text-[13px] leading-6 text-ink" data-id="element-3488" data-mp="3hb9cq8ujtu3a">
                {withMentions(comment.body, mentionable)}
              </p>

              {comment.mentions.length > 0 && <p className="mt-1.5 text-[12px] text-ink-muted" data-id="element-3489" data-mp="wixt23yca6w2">
                  {comment.mentions.map(mention => mention.displayName).join(', ')} notified through
                  their Campuzon notification preferences.
                </p>}

              {comment.replies.length > 0 && <ul className="mt-2.5 space-y-2 border-l-2 border-line pl-3" data-id="element-3490" data-mp="152ud2sof015n">
                  {comment.replies.map(reply => <li key={reply.id} data-id="element-3491" data-mp="q6d0xz8eiii1">
                      <p className="flex flex-wrap items-center gap-2" data-id="element-3492" data-mp="v44usyslweov">
                        <span className="text-[13px] font-semibold text-ink" data-id="element-3493" data-mp="1isxw7arzzeqv">{reply.authorName}</span>
                        <span className="cz-num text-[12px] text-ink-subtle" data-id="element-3494" data-mp="1nrhhra2oioip">{reply.postedOn}</span>
                      </p>
                      <p className="text-[13px] leading-6 text-ink" data-id="element-3495" data-mp="191th8b2bntw1">
                        {withMentions(reply.body, mentionable)}
                      </p>
                    </li>)}
                </ul>}

              {replyTo === comment.id && canComment && <form onSubmit={event => {
          event.preventDefault();
          if (!replyDraft.trim()) return;
          addComment(note.id, replyDraft.trim(), comment.id);
          setReplyDraft('');
          setReplyTo(null);
        }} className="mt-2.5" data-id="element-3496" data-mp="26jaar4hr09qf">
                  <Textarea rows={2} value={replyDraft} onChange={event => setReplyDraft(event.target.value)} placeholder="Reply…" aria-label="Reply" data-id="element-3497" data-mp="3ouicsnukqu7r" />
                  <div className="mt-2 flex gap-2" data-id="element-3498" data-mp="lbfdiznsakdp">
                    <Button type="submit" size="sm" variant="primary" disabled={!replyDraft.trim()} data-id="element-3499" data-mp="gcvnoo62kog2">
                      Reply
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setReplyTo(null)} data-id="element-3500" data-mp="314e6g8q04gd2">
                      Cancel
                    </Button>
                  </div>
                </form>}
            </li>)}
        </ul>}
    </section>;
}